/* eslint-disable @typescript-eslint/no-explicit-any */

import { Card, Skeleton } from '@radix-ui/themes'
import { useEffect, useState } from 'react'
import { useAppDispatch } from '../../reducers/store'
import { fetchDeviceFleetStats, selectStats } from '../../reducers/stats'
import { useDispatch, useSelector } from 'react-redux'
import StackedBar from '../charts/StackedBar'
import { RoleType } from '../../types/UserTypes'
import { selectUser } from '../../reducers/users'
import { getRoles, permitsRole } from '../../utils/roles'
import { useNavigate } from 'react-router-dom'
import { fetchDevicePolicies, selectSettings, setCurrentPage } from '../../reducers/settings'
import DonutChart from '../charts/DonutChart'
import DashboardEmpty from './DashboardEmpty'
import DashboardRolloutSkelton from './DashboardRolloutSkeleton'
import Highcharts from 'highcharts'
import { BarStat, PolicyPoint } from '../../types/StatTypes'
import LineChart from '../charts/LineChart'
import { IconCircleCheck, IconDevicesOff } from '@tabler/icons-react'

import lineChart from '../../assets/line-chart.png'
import donutChart from '../../assets/donut-chart.png'
import lineChartDark from '../../assets/line-chart-dark.png'
import donutChartDark from '../../assets/donut-chart-dark.png'

function DashboardRollout() {
  const di = useAppDispatch()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { dashboardFleetStat } = useSelector(selectStats)
  const { session, darkModeEnabled } = useSelector(selectUser)
  const { devicePolicies } = useSelector(selectSettings)
  const roles = getRoles(session)
  const [countIos, setCountIos] = useState(0)
  const [countAndroid, setCountAndroid] = useState(0)
  const [percentageIos, setPercentageIos] = useState(0)
  const [percentageAndroid, setPercentageAndroid] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [iosComplianceData, setIosComplianceData] = useState<
    { x: number; y: number; rawOutOfPolicy: number; rawInPolicy: number }[]
  >([])
  const [androidComplianceData, setAndroidComplianceData] = useState<
    { x: number; y: number; rawOutOfPolicy: number; rawInPolicy: number }[]
  >([])
  const [groupedOsVersionsAndroid, setGroupedOsVersionsAndroid] = useState([] as { version: string; count: number }[])
  const [iosColor, setIosColor] = useState('#5D7CFA')
  const [androidColor, setAndroidColor] = useState('#3AC9DB')
  const [textColor, setTextColor] = useState('#000000')
  const [showDevicePolicyCompliance, setShowDevicePolicyCompliance] = useState(false)

  useEffect(() => {
    setIosColor('#5D7CFA')
    setAndroidColor('#3AC9DB')
    if (darkModeEnabled) {
      setTextColor('#FFFFFF')
    } else {
      setTextColor('#000000')
    }
  }, [darkModeEnabled])

  useEffect(() => {
    di(fetchDeviceFleetStats())
    di(fetchDevicePolicies())
  }, [])

  useEffect(() => {
    setShowDevicePolicyCompliance((devicePolicies && devicePolicies.length > 0) ?? false)
  }, [devicePolicies])

  useEffect(() => {
    if (dashboardFleetStat) {
      const countIos = dashboardFleetStat.osVersionsIOS?.reduce(
        (sum: number, os: { version: string; count: number }) => sum + os.count,
        0
      )
      setCountIos(countIos)

      const countAndroid = dashboardFleetStat.osVersionsAndroid?.reduce(
        (sum: number, os: { version: string; count: number }) => sum + os.count,
        0
      )
      setCountAndroid(countAndroid)
      setIsLoading(false)

      const groupedOsVersionsAndroid = dashboardFleetStat.osVersionsAndroid?.reduce(
        (acc: { version: string; count: number }[], os) => {
          const existingVersion = acc.find((item) => item.version === os.version)
          if (existingVersion) {
            existingVersion.count += os.count
          } else {
            acc.push({ version: os.version, count: os.count })
          }
          return acc
        },
        []
      )

      setGroupedOsVersionsAndroid(groupedOsVersionsAndroid)

      const iosComplianceData = dashboardFleetStat?.iOSOutOfCompliancePerDay?.map((complianceData) => ({
        x: Date.parse(complianceData.date),
        y: policyPercentage(complianceData.inPolicyCount, complianceData.outOfPolicyCount),
        rawOutOfPolicy: complianceData.outOfPolicyCount,
        rawInPolicy: complianceData.inPolicyCount,
      }))
      setIosComplianceData(iosComplianceData)

      const androidComplianceData = dashboardFleetStat?.androidOutOfCompliancePerDay?.map((complianceData) => ({
        x: Date.parse(complianceData.date),
        y: policyPercentage(complianceData.inPolicyCount, complianceData.outOfPolicyCount),
        rawOutOfPolicy: complianceData.outOfPolicyCount,
        rawInPolicy: complianceData.inPolicyCount,
      }))
      setAndroidComplianceData(androidComplianceData)
    }
  }, [dashboardFleetStat])

  useEffect(() => {
    if (dashboardFleetStat) {
      const iosPercentage = (countIos / (countIos + countAndroid)) * 100
      const androidPercentage = (countAndroid / (countIos + countAndroid)) * 100
      setPercentageIos(parseFloat(iosPercentage.toFixed(2)))
      setPercentageAndroid(parseFloat(androidPercentage.toFixed(2)))
    }
  }, [countIos, countAndroid])

  const showLoadingSkeleton = () => {
    return (
      <div className="flex flex-col">
        <Skeleton className="h-7 w-20 mt-3" />
        <Skeleton className="h-4 w-36 mt-2" />
      </div>
    )
  }

  const manage = (stat: string) => {
    if (stat == 'SCIM') {
      navigate('/integrations')
    } else {
      dispatch(setCurrentPage(stat))
      localStorage.setItem('SETTINGS_PAGE_COOKIE', stat)
      navigate('/settings')
    }
  }

  const barStat = (barStat: BarStat) => {
    const total = barStat.left + barStat.right
    const leftPercentage = total !== 0 ? (barStat.left / total) * 100 : 0
    const rightPercentage = total !== 0 ? (barStat.right / total) * 100 : 0

    const getStatesByColor = (color: string) => {
      if (color === '#E5E5E5') {
        return { hover: { brightness: -0.05 } }
      }
      return { hover: { brightness: 0.1 } }
    }

    return (
      <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
        {barStat.placeholder ? (
          <div className="flex flex-col gap-2 items-center justify-center h-full p-4 text-center">
            <IconDevicesOff className="text-textTertiary" />
            <p className="text-xs font-semibold">{barStat.placeholder}</p>
            <p className="text-textTertiary text-xs max-w-80">{barStat.description}</p>
          </div>
        ) : (
          <>
            <div className="flex flex-col justify-between pt-4 px-4">
              <p>{barStat.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{barStat.subtitle}</p>
            </div>
            <StackedBar
              chartData={{
                series: [
                  { data: [rightPercentage], color: barStat.rightColor, states: getStatesByColor(barStat.rightColor) },
                  { data: [0.6], color: 'transparent' }, // Spacer
                  { data: [leftPercentage], color: barStat.leftColor, states: getStatesByColor(barStat.leftColor) },
                ],
              }}
            />
            {dashboardFleetStat && (
              <div className="pb-4 px-4 flex justify-between">
                <div className="flex flex-col text-xs flex">
                  <div className="text-gray-500 dark:text-gray-400">{barStat.leftTitle}</div>
                  <div className="font-medium">{barStat.left.toLocaleString()}</div>
                </div>
                <div className="flex flex-col text-xs flex">
                  <div className="text-gray-500 dark:text-gray-400">{barStat.rightTitle}</div>
                  <div className="font-medium text-right">{barStat.right.toLocaleString()}</div>
                </div>
              </div>
            )}
            {!dashboardFleetStat && showLoadingSkeleton()}
          </>
        )}
      </Card>
    )
  }

  const configureStat = (stat: string, link: string, configured: boolean, requiredRoles: RoleType[]) => {
    return (
      <div className="flex flex-row border dark:border-neutral-11 rounded p-3 w-full">
        <div className="flex flex-col flex-grow">
          <p className="text-xs text-gray-500 dark:text-gray-400">{stat}</p>
          <p className="text-sm">{configured ? 'Configured' : 'Not Configured'}</p>
        </div>
        {permitsRole(roles, requiredRoles) && (
          <div className="flex flex-col lg:flex-row gap-2">
            <div
              onClick={() => manage(stat)}
              className="h-8 px-3 bg-neutral-1 rounded border border-neutral-5 justify-center items-center gap-2 inline-flex cursor-pointer"
            >
              <div className="text-neutral-12 text-sm leading-tight">{configured ? 'Manage' : 'Configure'}</div>
            </div>
            <a
              href={link}
              target="_blank"
              className="text-xs underline h-8 px-3 py-2 text-center text-mainPurple dark:text-secondaryPurple cursor-pointer hover:underline"
            >
              Docs
            </a>
          </div>
        )}
      </div>
    )
  }

  function policyPercentage(inPolicyCount: number, outOfPolicyCount: number): number {
    if (inPolicyCount + outOfPolicyCount === 0) {
      return 0
    }
    const percentage = (outOfPolicyCount / (inPolicyCount + outOfPolicyCount)) * 100
    return parseFloat(percentage.toFixed(0))
  }

  function policyFormatter(this: Highcharts.Point): string {
    let tooltip = `${Highcharts.dateFormat('%b %e, %Y', this.x)}<br/>`
    const highChartPoint = this as any
    highChartPoint.points?.forEach(function (policyPoint: PolicyPoint) {
      const rawOutOfPolicy = policyPoint.point.rawOutOfPolicy
      const rawInPolicy = policyPoint.point.rawInPolicy
      tooltip += `<span style="color:${policyPoint.color}">\u25CF</span> ${policyPoint.series.name}: <b>${rawOutOfPolicy}</b> of <b>${rawOutOfPolicy + rawInPolicy} (${policyPercentage(rawInPolicy, rawOutOfPolicy)}%)</b><br/>`
    })
    return tooltip
  }

  return (
    <>
      {isLoading ? (
        <DashboardRolloutSkelton />
      ) : dashboardFleetStat && dashboardFleetStat.totalDeviceCount > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 w-full mb-4 border-0 m-0 p-0 bg-contentBackground gap-4">
            {barStat({
              title: 'Device Enrollment',
              subtitle: 'Enrolled vs. Pending',
              leftTitle: 'Enrolled',
              left: dashboardFleetStat?.enrolledDeviceCount ?? 0,
              leftColor: '#40C057',
              rightTitle: 'Pending',
              right: dashboardFleetStat?.invitedDeviceCount ?? 0,
              rightColor: '#E5E5E5',
              placeholder:
                dashboardFleetStat?.enrolledDeviceCount + dashboardFleetStat?.invitedDeviceCount > 0
                  ? undefined
                  : 'No Devices',
              description: 'See the number of devices enrolled vs. pending',
            })}
            {barStat({
              title: 'Enrolled Device Scans',
              subtitle: 'Scanned vs. Unscanned in the Last 30 Days',
              leftTitle: 'Scanned',
              left: dashboardFleetStat?.last30DaysScanCount ?? 0,
              leftColor: '#40C057',
              rightTitle: 'Unscanned',
              right: dashboardFleetStat?.last30DaysNoScanCount ?? 0,
              rightColor: '#FAB005',
              placeholder:
                dashboardFleetStat?.enrolledDeviceCount > 0
                  ? dashboardFleetStat?.last30DaysScanCount + dashboardFleetStat?.last30DaysNoScanCount > 0
                    ? undefined
                    : 'No Devices Scanned'
                  : 'No Devices Enrolled',
              description: 'See the number of devices scanned vs. not scanned in the last 30 days',
            })}

            {barStat({
              title: 'OS Version',
              subtitle: 'Up-to-Date vs. Outdated Devices',
              leftTitle: 'Up to Date',
              left:
                (dashboardFleetStat?.latestIosOsVersionCount ?? 0) +
                (dashboardFleetStat?.latestAndroidOsVersionCount ?? 0),
              leftColor: '#40C057',
              rightTitle: 'Outdated',
              right:
                (dashboardFleetStat?.outdatedIosOsVersionCount ?? 0) +
                (dashboardFleetStat?.outdatedAndroidOsVersionCount ?? 0),
              rightColor: '#FAB005',
              placeholder: dashboardFleetStat?.enrolledDeviceCount > 0 ? undefined : 'No Devices Enrolled',
              description: 'See the number of devices with up-to-date OS Versions',
            })}

            {barStat({
              title: 'OS Distribution',
              subtitle: 'iOS vs. Android Enrolled Devices',
              leftTitle: 'iOS',
              left: dashboardFleetStat?.iosDeviceCount ?? 0,
              leftColor: iosColor,
              rightTitle: 'Android',
              right: dashboardFleetStat?.androidDeviceCount ?? 0,
              rightColor: androidColor,
              placeholder: dashboardFleetStat?.enrolledDeviceCount > 0 ? undefined : 'No Devices Enrolled',
              description: 'See the number of devices running iOS or Android',
            })}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {showDevicePolicyCompliance && (
              <div className="w-full border-0 m-0 p-0 bg-contentBackground">
                <Card
                  size="2"
                  className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full"
                  variant="ghost"
                >
                  <div className="flex flex-col justify-between p-4 h-full">
                    <div className="flex flex-col justify-between">
                      <p>Devices out of Policy</p>
                      {dashboardFleetStat.enrolledDeviceCount > 0 &&
                      (iosComplianceData.reduce((sum, iosData) => sum + iosData.rawOutOfPolicy, 0) > 0 ||
                        androidComplianceData.reduce((sum, androidData) => sum + androidData.rawOutOfPolicy, 0) > 0) ? (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Percentage of devices out of policy on iOS vs. Android
                        </p>
                      ) : null}
                    </div>
                    {dashboardFleetStat.enrolledDeviceCount > 0 &&
                    (iosComplianceData.reduce((sum, iosData) => sum + iosData.rawOutOfPolicy, 0) > 0 ||
                      androidComplianceData.reduce((sum, androidData) => sum + androidData.rawOutOfPolicy, 0) > 0) ? (
                      <>
                        <LineChart
                          textColor={textColor}
                          height={250}
                          rangeDescription="Last 30 Days"
                          maxY={100}
                          format="{value}%"
                          formatter={policyFormatter}
                          chartData={{
                            categories: [],
                            series: [
                              {
                                name: 'iOS',
                                color: iosColor,
                                data: iosComplianceData,
                              },
                              {
                                name: 'Android',
                                color: androidColor,
                                data: androidComplianceData,
                              },
                            ],
                          }}
                        />
                      </>
                    ) : (
                      <div className="flex flex-col gap-2 items-center justify-center h-full min-h-[200px]">
                        {dashboardFleetStat.enrolledDeviceCount > 0 ? (
                          <>
                            <IconCircleCheck className="text-successGreen" />
                            <p className="text-xs font-semibold">No Devices out of Policy</p>
                          </>
                        ) : (
                          <>
                            <IconDevicesOff />
                            <p className="text-xs font-semibold">No Devices Enrolled</p>
                          </>
                        )}
                        <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                          See the number of devices out of policy over time
                        </p>
                        <img src={lineChart} alt="Line Chart" className="px-1.5 dark:hidden absolute" />
                        <img src={lineChartDark} alt="Line Chart" className="px-1.5 hidden dark:block absolute" />
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}
            <div className="w-full border-0 m-0 p-0 bg-contentBackground">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between p-4 h-full">
                  <p>OS Versions</p>
                  {dashboardFleetStat.enrolledDeviceCount > 0 ? (
                    <DonutChart
                      chartData={{
                        textColor: textColor,
                        name: 'OS',
                        categories: ['iOS', 'Android'],
                        data: [
                          {
                            raw: countIos,
                            y: percentageIos,
                            color: iosColor,
                            drilldown: {
                              name: 'iOS',
                              categories: dashboardFleetStat.osVersionsIOS?.map((os) => os.version),
                              data: dashboardFleetStat.osVersionsIOS?.map((os) => ({
                                y: parseFloat(((os.count / (countIos + countAndroid)) * 100).toFixed(2)),
                                raw: os.count,
                              })),
                            },
                          },
                          {
                            raw: countAndroid,
                            y: percentageAndroid,
                            color: androidColor,
                            drilldown: {
                              name: 'Android',
                              categories: groupedOsVersionsAndroid?.map((os) => os.version),
                              data: groupedOsVersionsAndroid?.map((os) => ({
                                y: parseFloat(((os.count / (countIos + countAndroid)) * 100).toFixed(2)),
                                raw: os.count,
                              })),
                            },
                          },
                        ],
                      }}
                    />
                  ) : (
                    <div className="flex flex-col gap-2 items-center justify-center h-full min-h-[200px]">
                      <IconDevicesOff />
                      <p className="text-xs font-semibold">No Devices Enrolled</p>
                      <p className="text-textTertiary text-xs px-4 max-w-80 text-center">
                        See the iOS and Android versions across your device fleet
                      </p>
                      <img src={donutChart} alt="Donut Chart" className="px-1.5 dark:hidden absolute" />
                      <img src={donutChartDark} alt="Donut Chart" className="px-1.5 hidden dark:block absolute" />
                    </div>
                  )}
                </div>
              </Card>
            </div>
            <div className="flex flex-col w-full m-0 p-0 bg-contentBackground gap-4">
              <div className="w-full border-0 m-0 p-0 bg-contentBackground gap-4">
                {barStat({
                  title: 'Members with Enrolled Devices',
                  subtitle: 'Members with 1 or more devices enrolled vs. members with none',
                  leftTitle: 'Members with an enrolled device',
                  left: dashboardFleetStat?.membersWithEnrolledDeviceCount ?? 0,
                  leftColor: '#40C057',
                  rightTitle: 'Members without an enrolled device',
                  right: dashboardFleetStat?.membersWithoutEnrolledDeviceCount ?? 0,
                  rightColor: '#E5E5E5',
                })}
              </div>
              {/* Commented out until extended protection rollout */}
              {/* <div className="w-full border-0 m-0 p-0 bg-contentBackground gap-4">
                {barStat({
                  title: 'iVerify Extended Protection',
                  subtitle: 'Devices enrolled with iVerify Extended Protection',
                  leftTitle: 'Paired',
                  left: dashboardFleetStat?.elitePairedCount ?? 0,
                  leftColor: '#40C057',
                  rightTitle: 'Not Paired',
                  right: (dashboardFleetStat?.eliteDeviceCount ?? 0) - (dashboardFleetStat?.elitePairedCount ?? 0),
                  rightColor: '#FAB005',
                  placeholder: dashboardFleetStat?.eliteDeviceCount > 0 ? '' : dashboardFleetStat.isEliteCustomer
                    ? 'Enrol a device with iVerify Extended Protection to start seeing data'
                    : "Contact our team to unlock iVerify's most advanced protection."
                })}
              </div> */}
            </div>
            <div className="w-full border-0 m-0 p-0 bg-contentBackground h-full">
              <Card size="2" className="p-0 m-0 bg-cardFill flex-grow border border-mainBorder h-full" variant="ghost">
                <div className="flex flex-col justify-between p-4 h-full">
                  <p className="mb-4">Configurations</p>
                  <div className="flex flex-col justify-evenly flex-grow gap-2">
                    {configureStat(
                      'MDM',
                      `${window.location.origin}/docs/iverify-portal-guide/Settings/Fleet-settings/Mobile-devices-MDM`,
                      dashboardFleetStat?.mdmConfigured ?? false,
                      [RoleType.PLATFORM_MGMT]
                    )}
                    {configureStat(
                      'SCIM',
                      `${window.location.origin}/docs/iverify-portal-guide/Integrations/`,
                      dashboardFleetStat?.scimConfigured ?? false,
                      [RoleType.PLATFORM_MGMT]
                    )}
                    {configureStat(
                      'SSO',
                      `${window.location.origin}/docs/iverify-portal-guide/Settings/Portal-settings/single-sign-on-sso/`,
                      dashboardFleetStat?.ssoConfigured ?? false,
                      [RoleType.PLATFORM_MGMT]
                    )}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </>
      ) : (
        <DashboardEmpty />
      )}
    </>
  )
}

export default DashboardRollout
